/**
 * Small fetch wrapper that:
 *   - Prefixes /api to the configured VITE_API_URL (or same-origin in dev via Vite proxy)
 *   - Sets JSON headers
 *   - Throws on non-2xx
 *   - Emite una entrada en el activity log del frontend por cada request /
 *     response / error, para que el panel de dev pueda mostrar qué está
 *     haciendo la app en tiempo real.
 */
import { log as devLog, _redact as redact } from './devLog.js'

const BASE = import.meta.env.VITE_API_URL || ''

// No loguear los POST que el propio log hace contra /dev/logs, si no se
// arma un loop infinito (cada log dispara un POST /dev/logs que a su vez
// se intenta loguear, etc.). Tampoco loguear el GET /dev/logs que usa el
// panel para refrescar, ya que inunda el propio log.
const SILENT_ENDPOINTS = /^\/dev\/logs(\/|$)/

export async function apiCall(endpoint, { method = 'GET', body, headers } = {}) {
  const url = `${BASE}/api${endpoint}`
  const opts = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(headers || {}),
    },
  }
  if (body !== undefined) opts.body = JSON.stringify(body)

  const silent = SILENT_ENDPOINTS.test(endpoint)
  const startedAt =
    typeof performance !== 'undefined' && performance.now
      ? performance.now()
      : Date.now()

  if (!silent) {
    devLog({
      level: 'info',
      phase: 'request',
      component: 'api',
      method,
      path: `/api${endpoint}`,
      message: `→ ${method} /api${endpoint}`,
      detail: body !== undefined ? { body: redact(body) } : undefined,
    })
  }

  let res
  try {
    res = await fetch(url, opts)
  } catch (err) {
    const durationMs = Math.round(
      (typeof performance !== 'undefined' && performance.now
        ? performance.now()
        : Date.now()) - startedAt
    )
    if (!silent) {
      devLog({
        level: 'error',
        phase: 'error',
        component: 'api',
        method,
        path: `/api${endpoint}`,
        durationMs,
        message: `✖ ${method} /api${endpoint} — network error: ${err?.message || err}`,
        detail: {
          name: err?.name,
          stack: err?.stack ? String(err.stack).split('\n').slice(0, 8) : undefined,
        },
      })
    }
    throw err
  }

  const durationMs = Math.round(
    (typeof performance !== 'undefined' && performance.now
      ? performance.now()
      : Date.now()) - startedAt
  )

  if (!res.ok) {
    const text = await res.text().catch(() => '')
    if (!silent) {
      devLog({
        level: res.status >= 500 ? 'error' : 'warn',
        phase: 'response',
        component: 'api',
        method,
        path: `/api${endpoint}`,
        status: res.status,
        durationMs,
        message: `← ${method} /api${endpoint} → ${res.status} (${durationMs}ms)`,
        detail: { body: text ? text.slice(0, 500) : undefined },
      })
    }
    throw new Error(`API ${method} ${endpoint} failed (${res.status}): ${text}`)
  }

  if (!silent) {
    devLog({
      level: 'info',
      phase: 'response',
      component: 'api',
      method,
      path: `/api${endpoint}`,
      status: res.status,
      durationMs,
      message: `← ${method} /api${endpoint} → ${res.status} (${durationMs}ms)`,
    })
  }

  // Some endpoints return 204 no content
  if (res.status === 204) return null
  return res.json()
}
